/**
 * Resume a saved project draft: hydrate the in-memory intake cache from its
 * payload and return where to navigate. The target screen then renders from the
 * now-populated cache (chat via usePersistedState; the form via getPersisted
 * defaultValues). Reuses the existing session-restore path rather than threading
 * the draft through navigation state.
 */
import { getDraft } from '@/api/project-drafts';
import { setPersisted } from '@/lib/intakeSession';

interface HistoryLike {
  files?: unknown[];
}

export async function resumeDraft(
  id: string,
): Promise<{ route: string; hadFiles: boolean }> {
  const draft = await getDraft(id);
  const prefix = draft.source; // 'chat' | 'form'
  // Each payload key maps to a "<prefix>.<key>" persisted-state slot.
  for (const [key, value] of Object.entries(draft.payload)) {
    setPersisted(`${prefix}.${key}`, value);
  }
  // Keep updating this same draft from the resumed screen.
  setPersisted(`${prefix}.draftId`, id);

  // Raw file objects aren't persisted, so flag if the user will want to re-attach.
  const history = draft.payload.history as HistoryLike[] | undefined;
  const hadFiles =
    draft.source === 'form' ||
    (Array.isArray(history) && history.some((t) => (t.files?.length ?? 0) > 0));

  const route = draft.source === 'chat' ? '/projects/new/chat' : '/projects/new';
  return { route, hadFiles };
}
